import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import {
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBTable,
  MDBTableHead,
  MDBTableBody,
  MDBSpinner,
  MDBBadge,
} from 'mdb-react-ui-kit'
import { format, parseISO } from 'date-fns'
import UniversityStore from '../../store/University.store'

import './Calendar.component.scss'

/**
 * Calendar Component
 *
 * Displays upcoming USABG University training sessions in a table/card format.
 * Shows session dates, locations, and availability information.
 */
const Calendar = observer(() => {
  useEffect(() => {
    if (UniversityStore.sessions.length === 0) {
      UniversityStore.fetchSessions()
    }
  }, [])

  const formatDateRange = (startDate, endDate) => {
    try {
      // Extract just YYYY-MM-DD portion and parse as local date (noon) to prevent timezone rollover
      const toLocalDate = dateStr => {
        const dateOnly = String(dateStr).split('T')[0].split(' ')[0]
        const [year, month, day] = dateOnly.split('-').map(Number)
        return new Date(year, month - 1, day)
      }
      const start = toLocalDate(startDate)
      const end = toLocalDate(endDate)
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`
    } catch {
      return `${startDate} - ${endDate}`
    }
  }

  const getAvailableSeats = session => {
    if (!session.max_capacity) return 'Unlimited'
    const enrolled = session.enrolled_count || 0
    const available = session.max_capacity - enrolled
    return `${available} / ${session.max_capacity}`
  }

  const getAvailabilityBadge = session => {
    if (!session.max_capacity) {
      return <MDBBadge color='success'>Open</MDBBadge>
    }

    const enrolled = session.enrolled_count || 0
    const available = session.max_capacity - enrolled

    if (available <= 0) {
      return <MDBBadge color='danger'>Full</MDBBadge>
    } else if (available <= 5) {
      return <MDBBadge color='warning'>Limited</MDBBadge>
    } else {
      return <MDBBadge color='success'>Available</MDBBadge>
    }
  }

  if (UniversityStore.isLoading.sessions) {
    return (
      <MDBCard id='UniversityCalendarComponent'>
        <MDBCardHeader className='calendar-header'>
          <i className='fas fa-calendar-alt mr-2'></i>
          Upcoming Sessions
        </MDBCardHeader>
        <MDBCardBody className='text-center py-5'>
          <MDBSpinner />
          <p className='mt-3'>Loading sessions...</p>
        </MDBCardBody>
      </MDBCard>
    )
  }

  if (UniversityStore.sessions.length === 0) {
    return (
      <MDBCard id='UniversityCalendarComponent'>
        <MDBCardHeader className='calendar-header'>
          <i className='fas fa-calendar-alt mr-2'></i>
          Upcoming Sessions
        </MDBCardHeader>
        <MDBCardBody className='text-center py-5'>
          <div className='empty-state'>
            <i className='fas fa-calendar-times fa-3x mb-3 text-muted'></i>
            <h5>No Upcoming Sessions</h5>
            <p className='text-muted'>
              There are no USABG University sessions scheduled at this time. Check back later for upcoming training
              opportunities.
            </p>
          </div>
        </MDBCardBody>
      </MDBCard>
    )
  }

  return (
    <MDBCard id='UniversityCalendarComponent'>
      <MDBCardHeader className='calendar-header'>
        <i className='fas fa-calendar-alt mr-2'></i>
        Upcoming Sessions
        <span className='badge badge-light ml-2'>{UniversityStore.sessions.length}</span>
      </MDBCardHeader>
      <MDBCardBody>
        <MDBTable responsive striped hover>
          <MDBTableHead>
            <tr>
              <th>Date</th>
              <th>Location</th>
              <th>Seats</th>
              <th>Status</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            {UniversityStore.sessions.map(session => (
              <tr key={session.id}>
                <td>
                  <strong>{formatDateRange(session.start_date, session.end_date)}</strong>
                </td>
                <td>
                  {session.location?.name || 'TBD'}
                  {session.location?.address && (
                    <small className='d-block text-muted'>{session.location.address}</small>
                  )}
                </td>
                <td>{getAvailableSeats(session)}</td>
                <td>{getAvailabilityBadge(session)}</td>
              </tr>
            ))}
          </MDBTableBody>
        </MDBTable>
      </MDBCardBody>
    </MDBCard>
  )
})

export default Calendar
